import React, { useEffect, useState } from "react";
import {
  getAttendance,
  getFees,
  getSalaries,
  getStudents,
  getTeachers
} from "../services/api";
import DateInput from "../components/DateInput";
import { getFeeCoverageForMonth } from "../utils/feeCoverage";
import "./HomePage.css";

const getTodayDate = () => new Date().toISOString().split("T")[0];
const getCurrentMonth = () => getTodayDate().slice(0, 7);

const parseDateKey = (dateKey) => {
  const [year, month, day] = String(dateKey).split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getShortDay = (dateKey) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" });

const getMonthLabel = (dateKey) =>
  new Date(`${dateKey}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short" });

const getRangeConfig = ({ type, singleDate, fromDate, toDate }) => {
  const today = getTodayDate();
  const todayDate = parseDateKey(today);

  if (type === "single") {
    const date = singleDate || today;
    return { fromDate: date, toDate: date, title: "Single Day" };
  }

  if (type === "monthly") {
    const start = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
    return { fromDate: toDateKey(start), toDate: today, title: "Monthly" };
  }

  if (type === "quarterly") {
    const quarterStartMonth = Math.floor(todayDate.getMonth() / 3) * 3;
    const start = new Date(todayDate.getFullYear(), quarterStartMonth, 1);
    return { fromDate: toDateKey(start), toDate: today, title: "Quarterly" };
  }

  if (type === "custom") {
    return {
      fromDate: fromDate || today,
      toDate: toDate || fromDate || today,
      title: "Custom Range"
    };
  }

  return { fromDate: toDateKey(addDays(todayDate, -6)), toDate: today, title: "7 Days" };
};

const getDaysBetween = (fromDate, toDate) => {
  const start = parseDateKey(fromDate);
  const end = parseDateKey(toDate);
  return Math.max(Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1, 1);
};

const filterRecordsByDateRange = (records, fromDate, toDate) =>
  records.filter((record) => record.date >= fromDate && record.date <= toDate);

const buildAttendanceTrend = (records, fromDate, toDate) => {
  const dayCount = getDaysBetween(fromDate, toDate);

  if (dayCount > 45) {
    const recordsByMonth = records.reduce((map, record) => {
      const month = String(record.date || "").slice(0, 7);
      const current = map.get(month) || { present: 0, total: 0 };
      current.total += 1;
      if (record.attendance_status === "present") {
        current.present += 1;
      }
      map.set(month, current);
      return map;
    }, new Map());

    const months = [];
    const cursor = new Date(parseDateKey(fromDate).getFullYear(), parseDateKey(fromDate).getMonth(), 1);
    const end = new Date(parseDateKey(toDate).getFullYear(), parseDateKey(toDate).getMonth(), 1);

    while (cursor <= end) {
      const month = toDateKey(cursor).slice(0, 7);
      const monthStats = recordsByMonth.get(month) || { present: 0, total: 0 };
      months.push({
        date: month,
        label: getMonthLabel(month),
        presentPercent: monthStats.total ? Math.round((monthStats.present / monthStats.total) * 100) : 0
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return months;
  }

  const recordsByDate = records.reduce((map, record) => {
    const date = record.date;
    const current = map.get(date) || { present: 0, total: 0 };
    current.total += 1;
    if (record.attendance_status === "present") {
      current.present += 1;
    }
    map.set(date, current);
    return map;
  }, new Map());

  return Array.from({ length: dayCount }, (_, index) => {
    const date = toDateKey(addDays(parseDateKey(fromDate), index));
    const dayStats = recordsByDate.get(date) || { present: 0, total: 0 };
    return {
      date,
      label: dayCount === 1 ? date.slice(5) : getShortDay(date),
      presentPercent: dayStats.total ? Math.round((dayStats.present / dayStats.total) * 100) : 0
    };
  });
};

function HomeQuickLinkIcon({ type }) {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  };

  const icons = {
    students: (
      <svg {...commonProps}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="9.5" cy="7" r="3.2" />
        <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M15.5 3.3a3.2 3.2 0 0 1 0 6.2" />
      </svg>
    ),
    teachers: (
      <svg {...commonProps}>
        <path d="m4 7 8-4 8 4-8 4-8-4Z" />
        <path d="M8 10.5v3.2c0 1.5 1.8 2.8 4 2.8s4-1.3 4-2.8v-3.2" />
        <path d="M20 9v5" />
        <circle cx="20" cy="16.5" r="1.2" />
      </svg>
    ),
    attendance: (
      <svg {...commonProps}>
        <rect x="4" y="5" width="16" height="15" rx="2" />
        <path d="M8 3v4M16 3v4M4 10h16" />
        <path d="m9 14 2 2 4-4" />
      </svg>
    ),
    fees: (
      <svg {...commonProps}>
        <path d="M12 2v20" />
        <path d="M17 6.5c0-1.7-2.2-3-5-3s-5 1.3-5 3 1.5 2.5 5 3 5 1.3 5 3-2.2 3-5 3-5-1.3-5-3" />
      </svg>
    ),
    reports: (
      <svg {...commonProps}>
        <path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
        <path d="M14 3v5h5" />
        <path d="M10 13h6M10 17h6M10 9h1" />
      </svg>
    ),
    salary: (
      <svg {...commonProps}>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
        <path d="M7 14h3M14 14h3" />
      </svg>
    )
  };

  return icons[type] || icons.students;
}

function AttendanceCircleChart({ present, absent, title }) {
  const total = present + absent;
  const presentPercent = total ? Math.round((present / total) * 100) : 0;
  const radius = 43;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (presentPercent / 100) * circumference;

  return (
    <div className="home-page__chart-card home-page__chart-card--circle">
      <div className="home-page__chart-head">
        <span>{title}</span>
        <strong>{total} Records</strong>
      </div>
      <div className="home-page__donut-wrap">
        <svg className="home-page__donut" viewBox="0 0 120 120" role="img" aria-label="Today attendance percentage">
          <circle className="home-page__donut-track" cx="60" cy="60" r={radius} />
          <circle
            className="home-page__donut-value"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="home-page__donut-center">
          <strong>{presentPercent}%</strong>
          <span>Present</span>
        </div>
      </div>
      <div className="home-page__chart-legend">
        <span><i className="home-page__legend-dot home-page__legend-dot--present" />{present} Present</span>
        <span><i className="home-page__legend-dot home-page__legend-dot--absent" />{absent} Absent</span>
      </div>
    </div>
  );
}

function AttendanceLineChart({ data, title }) {
  const width = 260;
  const height = 116;
  const padding = 18;
  const maxValue = Math.max(...data.map((item) => item.presentPercent), 100);
  const points = data.map((item, index) => {
    const x = data.length === 1
      ? width / 2
      : padding + (index * (width - padding * 2)) / (data.length - 1);
    const y = height - padding - (item.presentPercent / maxValue) * (height - padding * 2);
    return { ...item, x, y };
  });
  const polyline = points.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <div className="home-page__chart-card home-page__chart-card--line">
      <div className="home-page__chart-head">
        <span>{title}</span>
        <strong>Present %</strong>
      </div>
      <svg className="home-page__line-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Attendance trend line chart">
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} className="home-page__line-axis" />
        <polyline points={polyline} className="home-page__line-path" />
        {points.map((point) => (
          <g key={point.date}>
            <circle cx={point.x} cy={point.y} r="4" className="home-page__line-point" />
            <text x={point.x} y={height - 3} textAnchor="middle" className="home-page__line-label">
              {point.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function HomePage({
  adminSession,
  currentIstTime,
  festivals,
  onOpenFestival,
  onNavigate,
  formatToday,
  formatHolidayDate,
  fallbackHoliday
}) {
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    attendance: 0,
    absent: 0,
    unpaidFees: 0,
    unpaidSalary: 0
  });
  const [attendanceRangeType, setAttendanceRangeType] = useState("7days");
  const [attendanceSingleDate, setAttendanceSingleDate] = useState(getTodayDate());
  const [attendanceFromDate, setAttendanceFromDate] = useState(toDateKey(addDays(new Date(), -6)));
  const [attendanceToDate, setAttendanceToDate] = useState(getTodayDate());
  const [attendanceChartSummary, setAttendanceChartSummary] = useState({ present: 0, absent: 0, total: 0, title: "7 Days" });
  const [attendanceTrend, setAttendanceTrend] = useState([]);

  useEffect(() => {
    const loadHomeStats = async () => {
      try {
        const today = getTodayDate();
        const [studentsRes, teachersRes, attendanceRes, feesRes, salariesRes] = await Promise.all([
          getStudents("all", { fields: "lookup" }),
          getTeachers("all", { fields: "lookup" }),
          getAttendance(today, "all"),
          getFees({}),
          adminSession?.role === "admin" ? getSalaries({}) : Promise.resolve({ data: [] })
        ]);
        const currentMonth = getCurrentMonth();
        const feesByStudentId = feesRes.data.reduce((map, record) => {
          const studentId = String(record.student_id);
          const records = map.get(studentId) || [];
          records.push(record);
          map.set(studentId, records);
          return map;
        }, new Map());
        const studentsWithPendingFees = studentsRes.data.filter((student) => {
          const studentFeeRecords = feesByStudentId.get(String(student._id)) || [];
          return !getFeeCoverageForMonth(studentFeeRecords, currentMonth).isPaid;
        });

        setStats({
          students: studentsRes.data.length,
          teachers: teachersRes.data.length,
          attendance: attendanceRes.data.length,
          absent: attendanceRes.data.filter((record) => record.attendance_status === "absent").length,
          unpaidFees: studentsWithPendingFees.length,
          unpaidSalary: salariesRes.data.filter((record) => record.status !== "paid").length
        });
      } catch (err) {
        console.error("Failed to load home stats:", err);
      }
    };

    loadHomeStats();
  }, [adminSession]);

  useEffect(() => {
    const loadAttendanceCharts = async () => {
      try {
        const range = getRangeConfig({
          type: attendanceRangeType,
          singleDate: attendanceSingleDate,
          fromDate: attendanceFromDate,
          toDate: attendanceToDate
        });
        const res = await getAttendance();
        const records = filterRecordsByDateRange(res.data, range.fromDate, range.toDate);
        const present = records.filter((record) => record.attendance_status === "present").length;
        const absent = records.filter((record) => record.attendance_status === "absent").length;

        setAttendanceChartSummary({
          present,
          absent,
          total: records.length,
          title: range.title
        });
        setAttendanceTrend(buildAttendanceTrend(records, range.fromDate, range.toDate));
      } catch (err) {
        console.error("Failed to load attendance charts:", err);
      }
    };

    loadAttendanceCharts();
  }, [attendanceRangeType, attendanceSingleDate, attendanceFromDate, attendanceToDate]);

  const upcomingHoliday = festivals[0] || fallbackHoliday;
  const quickLinks = [
    {
      key: "students",
      icon: "students",
      title: "Students",
      value: `${stats.students} records`,
      description: "Profiles, classes, parent contacts, and student cards."
    },
    {
      key: "teachers",
      icon: "teachers",
      title: "Teachers",
      value: `${stats.teachers} records`,
      description: "Faculty details, subjects, and teacher profiles."
    },
    {
      key: "attendance",
      icon: "attendance",
      title: "Attendance",
      value: `${stats.absent} absent today`,
      description: "Date-wise attendance marking, review, and bulk actions."
    },
    {
      key: "fees",
      icon: "fees",
      title: "Fees",
      value: `${stats.unpaidFees} pending`,
      description: "Student fee records, payment cycles, and due tracking."
    },
    {
      key: "reports",
      icon: "reports",
      title: "Reports",
      value: "Excel exports",
      description: "Download student, teacher, and attendance reports."
    }
  ];

  if (adminSession?.role === "admin") {
    quickLinks.push({
      key: "salary",
      icon: "salary",
      title: "Salary",
      value: `${stats.unpaidSalary} pending`,
      description: "Teacher payout records, paid status, and monthly review."
    });
  }

  return (
    <div className="home-page">
      <div className="home-page__hero">
        <div className="home-page__today-card">
          <span className="home-page__today-label">Today</span>
          <strong>{formatToday()}</strong>
          <p>IST: {currentIstTime}</p>
        </div>

        <div className="home-page__holiday-card">
          <div className="home-page__holiday-copy">
            <span className="home-page__today-label">Upcoming Holiday</span>
            <strong>{upcomingHoliday.name}</strong>
            <p>{formatHolidayDate(upcomingHoliday.date)}</p>
          </div>
          <button className="home-page__holiday-action" onClick={onOpenFestival}>
            View All
          </button>
        </div>
      </div>

      <div className="home-page__section-head">
        <div>
          <h2>Attendance Overview</h2>
        </div>
      </div>

      <div className="home-page__chart-filters">
        <label className="home-page__chart-filter">
          <span>Range</span>
          <select value={attendanceRangeType} onChange={(e) => setAttendanceRangeType(e.target.value)}>
            <option value="7days">7 Days</option>
            <option value="single">Single Day</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="custom">From Date To Date</option>
          </select>
        </label>

        {attendanceRangeType === "single" && (
          <label className="home-page__chart-filter">
            <span>Date</span>
            <DateInput value={attendanceSingleDate} onChange={(e) => setAttendanceSingleDate(e.target.value)} />
          </label>
        )}

        {attendanceRangeType === "custom" && (
          <>
            <label className="home-page__chart-filter">
              <span>From</span>
              <DateInput value={attendanceFromDate} onChange={(e) => setAttendanceFromDate(e.target.value)} />
            </label>
            <label className="home-page__chart-filter">
              <span>To</span>
              <DateInput value={attendanceToDate} onChange={(e) => setAttendanceToDate(e.target.value)} />
            </label>
          </>
        )}
      </div>

      <div className="home-page__charts-grid">
        <AttendanceCircleChart
          present={attendanceChartSummary.present}
          absent={attendanceChartSummary.absent}
          title={attendanceChartSummary.title}
        />
        <AttendanceLineChart data={attendanceTrend} title={`${attendanceChartSummary.title} Trend`} />
      </div>

      <div className="home-page__section-head">
        <div>
          <h2>Quick Links</h2>
        </div>
      </div>

      <div className="home-page__links-grid">
        {quickLinks.map((item) => (
          <button
            key={item.key}
            className={`home-page__link-card home-page__link-card--${item.key}`}
            onClick={() => onNavigate(item.key)}
          >
            <span className="home-page__link-icon">
              <HomeQuickLinkIcon type={item.icon} />
            </span>
            <div className="home-page__link-copy">
              <strong>{item.title}</strong>
              <span>{item.value}</span>
              <p>{item.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default HomePage;
